import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Star } from "lucide-react";

let _cache = null;
let _pending = null;

// Real reviews from the Google Business Profile (server caches them for 12h). Shared by the hero + section.
export function useGoogleReviews() {
    const [data, setData] = useState(_cache);
    useEffect(() => {
        if (_cache) return;
        _pending = _pending || api.get("/reviews/google").then((r) => (_cache = r.data)).catch(() => (_cache = { enabled: false }));
        _pending.then((d) => setData(d));
    }, []);
    return data;
}

export function Stars({ n, size = 14 }) {
    return (
        <span className="inline-flex text-amber-400">
            {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={size} fill={i <= Math.round(n) ? "currentColor" : "none"} stroke="currentColor" />)}
        </span>
    );
}

export const GoogleG = () => (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
        <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
        <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
        <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 38.2 44 33 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
);

export function Review({ r }) {
    const [open, setOpen] = useState(false);
    const long = (r.text || "").length > 220;
    return (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-card p-5 flex flex-col">
            <div className="flex items-center gap-3">
                {r.photo ? <img src={r.photo} alt="" width={40} height={40} referrerPolicy="no-referrer" className="w-10 h-10 rounded-full object-cover" loading="lazy" />
                    : <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold">{(r.author || "?")[0]}</div>}
                <div className="min-w-0">
                    {r.author_url ? <a href={r.author_url} target="_blank" rel="noreferrer nofollow" className="font-semibold text-slate-900 hover:underline truncate block">{r.author}</a>
                        : <div className="font-semibold text-slate-900 truncate">{r.author}</div>}
                    <div className="flex items-center gap-2 text-xs text-slate-500"><Stars n={r.rating} size={12} /> {r.when}</div>
                </div>
                <span className="ml-auto"><GoogleG /></span>
            </div>
            <p className={`text-sm text-slate-600 mt-3 leading-relaxed whitespace-pre-line ${!open && long ? "line-clamp-5" : ""}`}>{r.text}</p>
            {long && <button onClick={() => setOpen((o) => !o)} className="text-xs font-semibold text-emerald-700 mt-1 self-start">{open ? "Show less" : "Read more"}</button>}
        </div>
    );
}

export default function GoogleReviews() {
    const d = useGoogleReviews();
    if (!d || !d.enabled || !d.rating) return null;      // nothing is shown unless Google returns real data
    return (
        <section id="reviews" className="py-20 bg-cream border-t border-slate-100">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
                    <div>
                        <div className="text-sm font-semibold text-emerald-600 mb-1">Reviews</div>
                        <h2 className="font-heading text-3xl font-bold text-slate-900">What our customers say on Google</h2>
                    </div>
                    <a href={d.url} target="_blank" rel="noreferrer" className="flex items-center gap-3 bg-white rounded-2xl border border-slate-100 shadow-card px-4 py-3 hover:shadow-md transition">
                        <GoogleG />
                        <div>
                            <div className="flex items-center gap-2"><span className="text-2xl font-bold text-slate-900">{Number(d.rating).toFixed(1)}</span><Stars n={d.rating} /></div>
                            <div className="text-xs text-slate-500">Based on {d.count} Google review{d.count === 1 ? "" : "s"} · See all →</div>
                        </div>
                    </a>
                </div>
                {d.reviews?.length > 0 && (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {d.reviews.slice(0, 6).map((r, i) => <Review key={`${r.author}-${i}`} r={r} />)}
                    </div>
                )}
                <div className="mt-6 text-center">
                    <a href={d.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-emerald-700">
                        Read all reviews or leave one on Google →
                    </a>
                </div>
            </div>
        </section>
    );
}
